import { parseSizeId, type CartGroup, type PricingData } from '@rpl/pricing';
import { useMemo, useState } from 'react';
import { formatMoney } from '../../lib/format';
import { checkPrint, thresholdsFromPricing, DEFAULT_THRESHOLDS } from '../../lib/printCheck';
import { asRotation, rotatedDims } from '../../lib/rotate';
import {
  defaultFrameColour,
  frameColourOptions,
  frameInColour,
  framePaperLabel,
  isCtPaper,
  sizeShort, sizeWithUnit,
} from '../../lib/sizePaper';
import type { LocalPhoto, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { Dropdown } from '../sizepaper/Dropdown';
import { FrameColourPicker } from '../sizepaper/FrameColourPicker';
import { CartModal } from './CartModal';
import { distinctPhotoIds } from './cartModel';
import { FramePreviewModes } from './frame/FramePreviewModes';
import { frameExplainer, frameOptionsForGroup } from './frame/frameOptions';
import { QtyStepper } from './QtyStepper';

/**
 * "Add frame" flow — a 2-step dialog with CONVERT semantics (iteration-2 fix:
 * framing must UPGRADE the print, not add a second one).
 *
 *   Step 1  Pick the photo to frame (thumbnail picker; auto-skipped when the
 *           group holds a single photo).
 *   Step 2  Frame preview + size picker + explainer + price + qty. Confirming
 *           CONVERTS `qty` units of that photo's loose print into a framed
 *           print via convertToFramed — the loose line loses those units
 *           (removed at 0), and the result is just the framed line (e.g. an
 *           8×10 goes from $15 loose + $74.95 framed = $89.95 down to a single
 *           $74.95 framed line). The framed print inherits the loose
 *           crop/fit/Print-Check.
 *
 * Any-paper upsell (owner 2026-07-26): the picker offers EVERY stocked framed
 * config — the group's paper is used where stocked, else the cheapest paper
 * stands in (labelled). It defaults to the group's own size+paper when that
 * config exists; picking one that differs warns that framing changes the
 * print's size/paper and convertToFramed moves the print onto the frame's own
 * size AND paper (crop resets when the aspect changes, Print Check reruns,
 * server reprices).
 */
export function AddFrameDialog({
  group,
  members,
  photosById,
  pricing,
  onClose,
  onReview,
  initialColour,
}: {
  group: CartGroup;
  members: UiPrint[];
  photosById: ReadonlyMap<string, LocalPhoto>;
  pricing: PricingData;
  onClose: () => void;
  /** Low-res handoff: open the crop editor on the print we just created.
   *  Owned by the CART screen, which outlives this dialog's card. */
  onReview: (printId: string) => void;
  /** Colour the upsell swatch preselected, so the customer is not asked twice. */
  initialColour?: string;
}) {
  const convertToFramed = useStore((s) => s.convertToFramed);
  const colours = useMemo(() => frameColourOptions(pricing), [pricing]);
  const [colour, setColour] = useState<string>(
    () => initialColour ?? defaultFrameColour(pricing),
  );
  const options = useMemo(
    () => frameOptionsForGroup(pricing, group, colour),
    [pricing, group, colour],
  );
  const photoIds = useMemo(() => distinctPhotoIds(members), [members]);
  const single = photoIds.length === 1;
  const oneOption = options.length <= 1;

  /** The group's own print size ("8x10") — the picker default + warning key. */
  const groupSize = useMemo(() => {
    try {
      return parseSizeId(group.sizeId).size;
    } catch {
      return null;
    }
  }, [group.sizeId]);

  const [photoId, setPhotoId] = useState<string | null>(single ? photoIds[0]! : null);
  const [step, setStep] = useState<1 | 2>(single ? 2 : 1);
  // Default to the frame matching the group's own size + paper (no change at
  // all) when stocked; else its size on a stand-in paper; else the smallest.
  const [optionIndex, setOptionIndex] = useState(() => {
    const exact = options.findIndex(
      (o) => o.printSize === groupSize && o.paper === group.paper,
    );
    if (exact >= 0) return exact;
    return Math.max(0, options.findIndex((o) => o.printSize === groupSize));
  });
  const [qty, setQty] = useState(1);

  const frameGroups = useMemo(
    () => [
      {
        label: 'Stocked frames',
        options: options.map((o) => ({
          key: o.frameId,
          label: `${sizeWithUnit(o.printSize)} print in ${sizeWithUnit(o.frameSize)} frame`,
          hint:
            pricing.productLines.framing.borderStyles[o.border] +
            (o.paper !== group.paper ? ` · ${framePaperLabel(pricing, o.paper)}` : ''),
          right: formatMoney(o.price),
        })),
      },
    ],
    [options, pricing, group.paper],
  );

  const option = options[optionIndex] ?? options[0];
  if (!option) return null; // group is not frameable (dialog shouldn't have opened)

  const explainer = frameExplainer(pricing, option);
  /** Chosen frame holds a different print size → warn before converting. */
  const sizeChanges = groupSize != null && option.printSize !== groupSize;
  /** Chosen frame prints on a different paper (cross-paper stand-in) → warn too. */
  const paperChanges = option.paper !== group.paper;
  const photo = photoId ? photosById.get(photoId) : undefined;
  // The loose print for the chosen photo — the line we convert. A group is one
  // size+paper, so there is exactly one loose print per photo. qty caps at it.
  const source = photoId ? members.find((m) => m.photoId === photoId) ?? null : null;
  const sourceQty = source?.qty ?? 0;
  const maxQty = Math.max(1, sourceQty);
  const clampedQty = Math.min(qty, maxQty);
  const total = clampedQty * option.price;

  /* What is WRONG with this photo at the chosen frame's print size (owner
     2026-07-27). Two things can be, and both need saying before we convert:

       'lowres' — the frame is bigger than the photo can carry. Converting
                  regardless produced a BLOCKED print, which is unpriced and so
                  vanishes from the cart: the line read as deleted and the
                  customer only found out at Continue.
       'crop'   — the frame's print is a different shape, so the photo gets
                  cropped. Silent auto-crop is what the Continue review exists
                  to catch, so offer the crop here instead.

     Either way the primary button becomes "Add frame" (it read "Fix" until
     2026-10-21, which customers took for an error), which converts and opens
     the crop editor on the result. */
  const issue = useMemo(() => {
    if (!photo || !source || photo.width <= 0 || photo.height <= 0) return null;
    const thresholds = pricing.printCheck
      ? thresholdsFromPricing(pricing.printCheck)
      : DEFAULT_THRESHOLDS;
    const [rw, rh] = rotatedDims(photo.width, photo.height, asRotation(source.rotation ?? 0));
    const family = isCtPaper(pricing, option.paper) ? 'ctype' : 'fine_art';
    // Same call the store makes on convert: the crop resets across a size
    // change, so judge the frame's print size on the full frame.
    const at = (inches: readonly [number, number]) =>
      checkPrint({ width: rw, height: rh }, [inches[0], inches[1]], null, 'crop', family, thresholds);
    const status = at(option.printInches).status;
    if (status === 'ok') return null;
    if (status === 'autocrop') return { kind: 'crop' as const, sharp: null };
    // The biggest frame this photo CAN carry sharply, so the warning offers a
    // way forward rather than just a dead end.
    const sharp = options
      .filter((o) => at(o.printInches).status !== 'lowres')
      .sort((a, b) => a.printInches[0] * a.printInches[1] - b.printInches[0] * b.printInches[1])
      .pop();
    return { kind: 'lowres' as const, sharp: sharp ?? null };
  }, [photo, source, option, options, pricing]);

  function choosePhoto(id: string) {
    setPhotoId(id);
    setQty(1);
    setStep(2);
  }

  function confirm() {
    if (!source) return;
    convertToFramed(source.id, clampedQty, option.frameId);
    onClose();
  }

  /* Add frame with an issue: convert, then hand the new print to the crop editor. It shows the
     photo inside the real frame with a live DPI readout, which answers "is
     this going to look bad" far better than a warning chip, and it has
     Save/Cancel rather than Remove, so the upsell survives. Low-res carries
     the customer's consent so the line stays PRICED and therefore visible; a
     crop-only issue needs no consent (the print is not blocked). */
  function frameAndFix() {
    if (!source) return;
    // convertToFramed hands back the line the print landed on (new, or the one
    // it merged into) — do not re-derive it with a predicate.
    const landedId = convertToFramed(
      source.id,
      clampedQty,
      option.frameId,
      issue?.kind === 'lowres',
    );
    if (landedId) onReview(landedId);
    onClose();
  }

  return (
    <CartModal
      title="Add a timber frame"
      caption={`${option.frameSize}" ${option.colourLabel.toLowerCase()} timber frame · real glass · ready to hang`}
      onClose={onClose}
      width={480}
    >
      <div className="framedlg">
        {step === 1 ? (
          <div className="framedlg__pick">
            <div className="framedlg__label">Which photo would you like framed?</div>
            <div className="framedlg__grid">
              {photoIds.map((id) => {
                const p = photosById.get(id);
                const selected = photoId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`framedlg__photo${selected ? ' framedlg__photo--on' : ''}`}
                    onClick={() => choosePhoto(id)}
                    title={p?.filename}
                    aria-pressed={selected}
                  >
                    {p?.previewUrl ? (
                      <img src={p.previewUrl} alt={p.filename} loading="lazy" />
                    ) : (
                      <span className="framedlg__ph" />
                    )}
                    {selected && <span className="framedlg__tick">✓</span>}
                  </button>
                );
              })}
            </div>
            <div className="cmodal__actions">
              <button type="button" className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="framedlg__step2">
            <FramePreviewModes photo={photo} option={option} />

            {/* No gate on a frame being picked, unlike ConfigurePane: this
                dialog exists BECAUSE the customer is adding a frame. The picker
                carries its own label, so the sizepick wrapper's would double up. */}
            <FrameColourPicker
              className="framedlg__colour"
              colours={colours}
              value={colour}
              onChange={(next) => {
                setColour(next);
                // Keep the customer's CONFIG, change only the moulding.
                const swapped = frameInColour(pricing, option.frameId, next);
                if (swapped) {
                  const i = frameOptionsForGroup(pricing, group, next).findIndex(
                    (o) => o.frameId === swapped.frameId,
                  );
                  if (i >= 0) setOptionIndex(i);
                }
              }}
            />

            {!oneOption && (
              <div className="framedlg__sizepick">
                <div className="framedlg__label">Frame size</div>
                <Dropdown
                  variant="light"
                  ariaLabel="Frame size"
                  groups={frameGroups}
                  value={option.frameId}
                  buttonLabel={`${sizeWithUnit(option.printSize)} print in ${sizeWithUnit(option.frameSize)} frame${paperChanges ? ` · ${framePaperLabel(pricing, option.paper)}` : ''} · ${formatMoney(option.price)}`}
                  onChange={(key) => {
                    const i = options.findIndex((o) => o.frameId === key);
                    if (i >= 0) setOptionIndex(i);
                  }}
                />
              </div>
            )}

            {(sizeChanges || paperChanges) && (
              <p className="framedlg__sizewarn">
                {sizeChanges && paperChanges ? (
                  <>
                    Framing changes your print to a {sizeWithUnit(option.printSize)}{' '}
                    {framePaperLabel(pricing, option.paper)} print to fit this frame.
                  </>
                ) : sizeChanges ? (
                  <>
                    Framing changes the print size to {sizeWithUnit(option.printSize)} to fit
                    this frame.
                  </>
                ) : (
                  <>
                    Framing prints this on {framePaperLabel(pricing, option.paper)} to suit this
                    frame.
                  </>
                )}
              </p>
            )}

            {issue && (
              <div
                className={`framedlg__alert framedlg__alert--${issue.kind}`}
                role={issue.kind === 'lowres' ? 'alert' : undefined}
              >
                {issue.kind === 'lowres' ? (
                  <>
                    <strong>
                      This photo isn&rsquo;t sharp enough for a {sizeWithUnit(option.printSize)}
                      print.
                    </strong>{' '}
                    {issue.sharp
                      ? `The largest frame it prints sharply in is ${sizeWithUnit(issue.sharp.frameSize)}.`
                      : 'It will look soft at every frame size we stock.'}
                    {issue.sharp && (
                      <button
                        type="button"
                        className="framedlg__alert-swap"
                        onClick={() => {
                          const i = options.findIndex((o) => o.frameId === issue.sharp!.frameId);
                          if (i >= 0) setOptionIndex(i);
                        }}
                      >
                        Use the {sizeWithUnit(issue.sharp.frameSize)} frame
                      </button>
                    )}
                  </>
                ) : (
                  <strong>
                    This photo isn&rsquo;t the shape of a {sizeWithUnit(option.printSize)} print,
                    so some of it gets cropped.
                  </strong>
                )}
                <span className="framedlg__alert-next">
                  Add frame then opens the crop so you can set it yourself and see exactly how
                  it prints. Leave it as it is and we centre it for you.
                </span>
              </div>
            )}

            <p className="framedlg__explain">
              {/* "This size is only available as..." is wrong when the single
                  option is a DIFFERENT print size/paper (it would contradict
                  the change warning above), so fall back to "Framed as". */}
              {oneOption && !sizeChanges && !paperChanges ? (
                <>
                  This size is only available as a <strong>{explainer.label}</strong> frame.
                </>
              ) : (
                <>
                  Framed as <strong>{explainer.label}</strong>.
                </>
              )}{' '}
              {explainer.detail}
            </p>

            <p className="framedlg__body">
              This <strong>upgrades your print into a framed print</strong>, print included.{' '}
              {clampedQty >= maxQty
                ? `Your ${sourceQty} loose ${group.displayProps['Size'] ?? group.sizeId} print${sourceQty === 1 ? '' : 's'} moves into the frame; the loose line is removed.`
                : `${clampedQty} of your ${sourceQty} loose ${group.displayProps['Size'] ?? group.sizeId} prints moves into a frame; the rest stay loose.`}
            </p>

            <div className="framedlg__row">
              <QtyStepper
                value={clampedQty}
                onInc={() => setQty((q) => Math.min(maxQty, q + 1))}
                onDec={() => setQty((q) => Math.max(1, q - 1))}
                onSet={(next) => setQty(next)}
                min={1}
                max={maxQty}
                decDisabled={clampedQty <= 1}
                label="framed prints"
              />
              <span className="framedlg__price">
                {formatMoney(option.price)} <span>· print included</span>
              </span>
            </div>
            {maxQty > 1 && (
              <div className="framedlg__hint">You can frame up to {maxQty} (your loose print count).</div>
            )}

            <div className="cmodal__actions">
              {!single && (
                <button type="button" className="btn btn--secondary" onClick={() => setStep(1)}>
                  ← Back
                </button>
              )}
              <button
                type="button"
                className="btn btn--primary"
                onClick={issue ? frameAndFix : confirm}
                disabled={!source}
              >
                {issue ? 'Add frame' : `Frame ${clampedQty > 1 ? `${clampedQty} prints` : 'this print'}`}{' '}
                · {formatMoney(total)}
              </button>
            </div>
          </div>
        )}
      </div>
    </CartModal>
  );
}
